import type { ReactNode } from "react";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import { goToApp } from "../siteLinks";

type CtaButton = { label: string; to?: string; onClick?: () => void };

type Props = {
    eyebrow?: string;
    heading: string;
    body: string;
    primary?: CtaButton;
    secondary?: CtaButton;
    /** Small muted note shown under the buttons — e.g. the tour
     *  template's "Curated by …" credit. Omitted when not set. */
    footnote?: ReactNode;
};

// Dark-teal full-bleed closing CTA band — "How Planamoto fits" (nav
// guides), "Ride this trip yourself" (ride report) — with a gold
// primary + translucent-outline secondary button, reusing the same
// scoped AntD button treatment as the landing page's teal steps band
// (.gt-steps-band__ctas). Defaults match the nav guides' original CTA.
export default function GuideCtaBand({
    eyebrow = "How Planamoto fits",
    heading,
    body,
    primary = { label: "Browse tours", to: "/explore" },
    secondary = { label: "Start your own" },
    footnote,
}: Props) {
    const navigate = useNavigate();
    const go = (btn: CtaButton) => {
        if (btn.onClick) return btn.onClick();
        if (btn.to) return navigate(btn.to);
        return goToApp("/signup");
    };
    return (
        <section className="gt-guide-cta-band">
            <div className="gt-guide-cta-band__inner">
                <div className="gt-eyebrow gt-guide-cta-band__eyebrow">{eyebrow}</div>
                <h2 className="gt-guide-cta-band__heading">{heading}</h2>
                <p className="gt-guide-cta-band__body">{body}</p>
                <div className="gt-guide-cta-band__buttons">
                    <Button type="primary" size="large" onClick={() => go(primary)}>
                        {primary.label}
                    </Button>
                    <Button size="large" onClick={() => go(secondary)}>
                        {secondary.label}
                    </Button>
                </div>
                {footnote && (
                    <div className="gt-guide-cta-band__footnote">{footnote}</div>
                )}
            </div>
        </section>
    );
}
